<template>
  <v-container fluid>
    <!-- Title -->
    <Title
        title="Lista de expedientes"
        :description="'Registro de los' + (totalItems > 1 ? ' ' + totalItems+ ' ' : ' ') +
        'expedientes en ' + (user.role === 'DIETICIAN' || officeFilter !== 0 ? 'el consultorio.' : 'todos los consultorios.')"
    />

    <v-row>
      <!-- Search bar -->
      <v-col
          cols="12"
          :md="isAdmin ? '5' : '7'"
      >
        <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Buscar"
            outlined
            dense
            clearable
            hide-details
            class="white"
            @input="getClientsPage"
            @click:clear="resetFilters"
        >
        </v-text-field>
      </v-col>

      <!-- Filter by office -->
      <v-col v-if="isAdmin" cols="12" md="2">
        <v-select
            v-model="officeFilter"
            :items="officesList"
            item-text="name"
            item-value="id"
            label="Filtrar por consultorio"
            outlined
            dense
            hide-details
            class="white"
        >
        </v-select>
      </v-col>

      <!-- Filter by status -->
      <v-col cols="12" md="2">
        <v-select
            v-model="statusFilter"
            :items="[
              { text: 'Habilitado', value: true },
              { text: 'Deshabilitado', value: false }
            ]"
            label="Filtrar por estado"
            outlined
            dense
            hide-details
            class="white"
        >
        </v-select>
      </v-col>

      <!-- Create button -->
      <v-col cols="12" md="3" class="d-flex align-center" v-if="isAllowed(PERMISSIONS.CLIENT_CONSULT)">
        <v-btn block color="primary" height="40" @click="openDialogCreateClient">
          <v-icon left>mdi-plus-box</v-icon>
          Registrar expediente
        </v-btn>
      </v-col>

      <!-- Create client modal -->
      <DialogCreateClient ref="modalCreateClient" @created="openDialogNewQuantityTable"
                          v-if="isAllowed(PERMISSIONS.CLIENT_CONSULT)"/>

      <!-- New quantity table modal -->
      <DialogNewQuantityTable
          v-model="isNewQuantityTableDialogVisible"
          @printed="getClientsPage"
          :clientInfo="newClientTableInfo"
          :quantityTableProp="newClientQuantityTable"
          v-if="isAllowed(PERMISSIONS.CLIENT_CONSULT)"
          :hasEatenLunchOrDinner="false"
      />
    </v-row>

    <!-- Clients Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
            class="border-px"
            :headers="headers"
            :items="clients"
            :loading="isLoading"
            :items-per-page="pageSize"
            :page="pageNumber + 1"
            :search="searchQuery"
            :server-items-length="totalItems"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            disable-filtering
            no-data-text="No se encontraron expedientes"
            no-results-text="No se encontraron expedientes"
            loading-text="Cargando expedientes..."
            :footer-props="{
              itemsPerPageOptions: [5, 10, 15, 20, 25, 50],
              showFirstLastPage: true,
              itemsPerPageText: 'Expedientes por página',
              itemsPerPageAllText: 'Todos',
              pageText: `Página ${pageNumber + 1 } de ${totalPages}`,
              rowsPerPageText: 'Expedientes por página',
            }"
            @update:options="handleTableOptions"
        >
          <template v-slot:item.name="{ item }">
            <v-chip
                x-small
                color="primary"
                class="me-2"
                label
            >
              {{ item.currentLevel + item.currentClass }}
            </v-chip>
            <span>
              {{ item.name }}
            </span>
            <v-icon small class="ms-2" v-if="item.birthday" color="pink">mdi-cake-variant</v-icon>
            <v-chip v-if="!item.validated" small color="orange lighten-5 orange--text" class="ms-2">
              <v-icon small left>mdi-alert-circle</v-icon>
              Sin validar
            </v-chip>
          </template>
          <template v-slot:item.officeName="{ item }" v-if="user.role !== 'DIETICIAN'">
            <span v-if="item.officeName">
                  <span class="deep-orange--text font-weight-medium">
                    DIETEC
                  </span>
                  <span>
                    {{ displayOfficeForAdmins(item) }}
                  </span>
                </span>
            <span v-else class="grey--text">
                  Sin consultorio
                </span>
          </template>
          <template v-slot:item.currentWeightKg="{ item }">
            <span>{{ item.currentWeightKg.toFixed(3) }} kg</span>
          </template>
          <template v-slot:item.lostWeightKg="{ item }">
            <span :class="weightLossColor(item.lostWeightKg)">{{
                item.lostWeightKg <= 0 ? item.lostWeightKg.toFixed(3) : '+' + item.lostWeightKg.toFixed(3)
              }} kg</span>
          </template>
          <template v-slot:item.nextAppointment="{ item }">
            <span>{{ formatDate(item.nextAppointment) }}</span>
          </template>
          <template v-slot:item.clientType="{ item }">
            <v-chip small :ripple="false" :class="clientTypeColor(item.clientType)">
              {{ item.clientType === 'REMOTE' ? 'En línea' : 'Presencial' }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <StatusChip :status="item.status"/>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <v-btn v-if="item.status || isAdmin" small class="me-2 white--text" color="green"
                     @click="handleItemClick(item)">
                <v-icon small left>mdi-file-account</v-icon>
                Ver
              </v-btn>
              <v-btn
                  small
                  color="primary"
                  @click="handleConsultation(item)"
                  v-if="isAllowed(PERMISSIONS.CLIENT_CONSULT) && item.status"
                  :disabled="!item.validated"
              >
                <v-icon small left>mdi-file-document-edit</v-icon>
                Consulta
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";

import DialogCreateClient from "@/views/clients/components/DialogCreateClient.vue";
import DialogNewQuantityTable from "@/views/clients/components/DialogNewQuantityTable.vue";
import Title from "@/components/Title.vue";
import StatusChip from "@/components/StatusChip.vue";

import ClientService from "@/services/clients/ClientService";
import OfficeService from "@/services/offices/OfficeService";

import {PERMISSIONS} from "@/utils/Permissions";
import {ClientForPage} from "@/services/clients/entities/ClientForPage";
import {RequestClientPage} from "@/services/clients/entities/RequestClientPage";
import {QuantityTableInfo} from "@/services/quantityTables/entities/QuantityTableInfo";
import {ClientTableInfo} from "@/services/clients/entities/ClientTableInfo";
import {OfficeName} from "@/services/offices/entities/OfficeName";

import {clientTypeColorClass, formatDateTimeToDisplay, isAllowed} from "@/utils/UniversalFunctions";
import throwErrorMessage from "@/utils/ErrorMessage";

export default Vue.extend({
  name: "ClientsTable",
  components: {
    StatusChip,
    Title,
    DialogCreateClient,
    DialogNewQuantityTable,
  },
  data() {
    return {
      // Loading
      isLoading: false as boolean,
      isLoadingOffices: false as boolean,

      // Table
      clients: [] as ClientForPage[],
      totalItems: 0 as number,
      totalPages: 0 as number,
      officesList: [] as OfficeName[],
      headers: [
        {text: 'Nombre y nivel', value: 'name', sortable: true},
        {text: 'Peso actual', value: 'currentWeightKg', sortable: true},
        {text: 'Peso perdido', value: 'lostWeightKg', sortable: false},
        {text: 'Siguiente cita', value: 'nextAppointment', sortable: true},
        {text: 'Tipo', value: 'clientType', sortable: true},
        {text: 'Estado', value: 'status', sortable: false},
        {text: 'Acciones', value: 'actions', sortable: false},
      ],

      // New quantity table data
      newClientTableInfo: {} as ClientTableInfo,
      newClientQuantityTable: {} as QuantityTableInfo,

      // References to modals
      modalCreateClient: null as Vue | null,
      isNewQuantityTableDialogVisible: false as boolean,
    }
  },

  watch: {
    searchQuery() {
      this.$store.commit("clients/RESET_PAGE_NUMBER")
      this.getClientsPage();
    },
    statusFilter() {
      this.$store.commit("clients/RESET_PAGE_NUMBER")
      this.getClientsPage();
    },
    officeFilter() {
      this.$store.commit("clients/RESET_PAGE_NUMBER")
      this.getClientsPage();
    },
    pageSize() {
      this.$store.commit("clients/RESET_PAGE_NUMBER")
    },
  },

  computed: {
    PERMISSIONS() {
      return PERMISSIONS
    },
    user() {
      return this.$store.getters['auth/getUser']
    },
    isAdmin(): boolean {
      return this.user.role !== 'DIETICIAN';
    },
    searchQuery: {
      get(): string {
        return this.$store.getters['clients/getSearchQuery'];
      },
      set(value: string) {
        this.$store.commit('clients/SET_SEARCH_QUERY', value);
      }
    },
    statusFilter: {
      get(): boolean {
        return this.$store.getters['clients/getStatusFilter'];
      },
      set(value: boolean) {
        this.$store.commit('clients/SET_STATUS_FILTER', value);
      }
    },
    officeFilter: {
      get(): number {
        return this.$store.getters['clients/getOfficeFilter'];
      },
      set(value: number) {
        this.$store.commit('clients/SET_OFFICE_FILTER', value);
      }
    },
    pageSize: {
      get(): number {
        return this.$store.getters['clients/getPageSize'];
      },
      set(value: number) {
        this.$store.commit('clients/SET_PAGE_SIZE', value);
      }
    },
    pageNumber: {
      get(): number {
        return this.$store.getters['clients/getPageNumber'];
      },
      set(value: number) {
        this.$store.commit('clients/SET_PAGE_NUMBER', value);
      }
    },
    sortBy: {
      get(): string {
        return this.$store.getters['clients/getSortBy'];
      },
      set(value: string) {
        this.$store.commit('clients/SET_SORT_BY', value);
      }
    },
    sortDesc: {
      get(): boolean {
        return this.$store.getters['clients/getSortDesc'];
      },
      set(value: boolean) {
        this.$store.commit('clients/SET_SORT_DESC', value);
      }
    },
  },

  methods: {
    async getClientsPage() {
      this.isLoading = true;
      this.clients = [];
      try {
        const body: RequestClientPage = {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          searchQuery: this.searchQuery,
          status: this.statusFilter,
          office: this.officeFilter,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }
        const response = await ClientService.getClientPage(body);
        this.clients = response.content;
        this.totalItems = response.totalElements;
        this.totalPages = response.totalPages;
      } finally {
        this.isLoading = false;
      }
    },
    async getOfficesList() {
      this.isLoadingOffices = true;
      try {
        this.officesList.push({id: 0, name: 'Todos'});
        const offices = await OfficeService.getAvailableOffices();
        this.officesList.push(...offices);
      } finally {
        this.isLoadingOffices = false;
      }
    },
    isAllowed(permissionName: string) {
      return isAllowed(permissionName);
    },
    openDialogCreateClient() {
      if (this.$refs.modalCreateClient) {
        (this.$refs.modalCreateClient as Vue & { dialog: boolean }).dialog = true;
      }
    },
    openDialogNewQuantityTable(data: any) {
      this.newClientTableInfo = data.clientTableInformation;
      this.newClientQuantityTable = data.quantityTable;
      this.isNewQuantityTableDialogVisible = true;
    },
    weightLossColor(lostWeightKg: number) {
      if (lostWeightKg === 0) {
        return '';
      }
      return lostWeightKg < 0 ? 'success--text' : 'red--text';
    },
    clientTypeColor(clientType: string) {
      return clientTypeColorClass(clientType);
    },
    formatDate(date: Date) {
      return formatDateTimeToDisplay(date);
    },
    handleItemClick(item: ClientForPage) {
      this.$router.push(`/expedientes/${item.publicId}`);
    },
    handleConsultation(item: ClientForPage) {
      if (!item.validated) {
        throwErrorMessage('El expediente aún no ha sido validado. Ponte en contacto con un administrador.');
        return;
      }
      this.$router.push(`/expedientes/consulta/${item.publicId}`);
    },
    handleTableOptions(options: any) {
      this.pageNumber = options.page - 1;
      this.pageSize = options.itemsPerPage;
      this.sortBy = options.sortBy.length > 0 ? options.sortBy[0] : 'nextAppointment';
      this.sortDesc = options.sortDesc.length > 0 ? options.sortDesc[0] : false;
      this.getClientsPage();
    },
    displayOfficeForAdmins(item: ClientForPage) {
      if (this.user.role == 'DIETICIAN') return '';
      if (item.officeName) return `${item.officeName}`;
      return 'Sin consultorio';
    },
    resetFilters(): void {
      this.$store.commit('clients/RESET_FILTERS');
    }
  },

  mounted() {
    if (this.user.role !== 'DIETICIAN') {
      this.headers.splice(1, 0, {text: 'Consultorio', value: 'officeName', sortable: false});
      this.getOfficesList();
    }
  },
});
</script>

<style scoped>
.border-px {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>